export type PromptMode = 'JAPANESE' | 'ENGLISH';

export interface AnswerCheckVocable {
  japanese?: string | null;
  kana?: string | null;
  english?: string[];
  conjugations?: Record<string, string>;
  kanjiMapping?: Record<string, string>;
}

/**
 * Internal cached canvas context for measuring text widths.
 */
let measurementContext: CanvasRenderingContext2D | null = null;

function getMeasurementContext(): CanvasRenderingContext2D | null {
  if (measurementContext) {
    return measurementContext;
  }

  if (typeof document === 'undefined') {
    return null;
  }

  const canvas = document.createElement('canvas');
  measurementContext = canvas.getContext('2d');
  return measurementContext;
}

export function measureTextWidth(text: string, font: string): number {
  const context = getMeasurementContext();

  if (!context) {
    // Fallback approximation if canvas is unavailable (e.g., during SSR)
    return text.length * 12;
  }

  context.font = font;
  return context.measureText(text).width;
}

export function buildAnswerWithinWidth(
  parts: string[],
  maxWidth?: number | null,
  font?: string
): string {
  const filteredParts = parts
    .map((part) => part?.trim() ?? '')
    .filter((part) => part.length > 0);

  if (filteredParts.length === 0) {
    return '';
  }

  if (!maxWidth || !font) {
    return filteredParts.join(', ');
  }

  let assembled = '';

  for (const part of filteredParts) {
    const candidate = assembled ? `${assembled}, ${part}` : part;
    const width = measureTextWidth(candidate, font);

    if (width > maxWidth) {
      if (!assembled) {
        return part;
      }
      break;
    }

    assembled = candidate;
  }

  return assembled || filteredParts[0];
}

/**
 * Generates all possible combinations from a string with optional parts in parentheses.
 * Example: "(the) house" -> ["the house", "house"]
 * Handles nested parentheses: "the (big (red)) house" -> ["the big red house", "the big house", "the house"]
 */
function generateOptionalCombinations(text: string): string[] {
  const combinations: Set<string> = new Set();
  
  // Recursive function to generate all combinations
  function generate(str: string): void {
    // Find the first opening parenthesis
    const openParen = str.indexOf('(');
    
    if (openParen === -1) {
      // No more parentheses, normalize and add
      const result = str.replace(/\s+/g, ' ').trim();
      if (result) {
        combinations.add(result);
      }
      return;
    }
    
    // Find the matching closing parenthesis
    let depth = 1;
    let closeParen = openParen + 1;
    while (closeParen < str.length && depth > 0) {
      if (str[closeParen] === '(') {
        depth++;
      } else if (str[closeParen] === ')') {
        depth--;
      }
      closeParen++;
    }
    
    if (depth === 0) {
      // Found matching parentheses
      const before = str.substring(0, openParen);
      const optional = str.substring(openParen + 1, closeParen - 1);
      const after = str.substring(closeParen);
      
      // Generate combinations with and without the optional part
      // Recursively process nested parentheses in each version
      generate(before + optional + after);
      generate(before + after);
    } else {
      // No matching closing parenthesis, treat as regular text
      const result = str.replace(/\s+/g, ' ').trim();
      if (result) {
        combinations.add(result);
      }
    }
  }
  
  generate(text);
  return combinations.size > 0 ? Array.from(combinations) : [text.replace(/\s+/g, ' ').trim()];
}

/**
 * Calculates the Levenshtein distance between two strings.
 */
function levenshteinDistance(str1: string, str2: string): number {
  const len1 = str1.length;
  const len2 = str2.length;
  
  // Create a matrix
  const matrix: number[][] = [];
  
  // Initialize first row and column
  for (let i = 0; i <= len1; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }
  
  // Fill the matrix
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,     // deletion
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j - 1] + 1  // substitution
        );
      }
    }
  }
  
  return matrix[len1][len2];
}

/**
 * Converts kanji text to kana using the kanjiMapping.
 * This function attempts to find the longest matching kanji compounds in the mapping.
 */
function convertKanjiToKana(text: string, kanjiMapping: Record<string, string>): string {
  if (!text || !kanjiMapping) {
    return text;
  }

  let result = '';
  let i = 0;

  while (i < text.length) {
    let matched = false;
    let longestMatch = '';
    let longestKana = '';

    // Try to find the longest kanji match starting from position i
    for (let len = text.length - i; len > 0; len--) {
      const candidate = text.substring(i, i + len);
      const kanaReading = kanjiMapping[candidate];
      
      if (kanaReading) {
        if (candidate.length > longestMatch.length) {
          longestMatch = candidate;
          longestKana = kanaReading;
          matched = true;
        }
      }
    }

    if (matched) {
      result += longestKana;
      i += longestMatch.length;
    } else {
      // No mapping found, check if it's a kanji character
      const char = text[i];
      const charCode = char.charCodeAt(0);
      const isKanji = (charCode >= 0x4E00 && charCode <= 0x9FFF) || 
                      (charCode >= 0x3400 && charCode <= 0x4DBF);
      
      if (isKanji) {
        // Try single character mapping
        const singleCharKana = kanjiMapping[char];
        if (singleCharKana) {
          result += singleCharKana;
        } else {
          // No mapping found, keep original character
          result += char;
        }
      } else {
        // Not a kanji, keep as is (hiragana/katakana/other)
        result += char;
      }
      i++;
    }
  }

  return result;
}

/**
 * Determines whether the provided answer matches the expected value for the given prompt mode.
 * @param promptMode - The mode of the prompt (JAPANESE or ENGLISH)
 * @param userInput - The user's input answer
 * @param vocable - The vocable being tested
 * @param includedConjugations - Optional list of verb form names that are allowed as correct answers (e.g., ["PRESENT_DICTIONARY", "PRESENT_POLITE"])
 */
export function isAnswerCorrect(
  promptMode: PromptMode,
  userInput: string,
  vocable: AnswerCheckVocable,
  includedConjugations?: string[]
): boolean {
  if (!userInput) {
    return false;
  }

  if (promptMode === 'ENGLISH') {
    const normalizedInput = userInput.trim();
    if (!normalizedInput) {
      return false;
    }

    const possibleAnswers = [vocable.japanese, vocable.kana].filter(
      (answer): answer is string => Boolean(answer && answer.trim().length > 0)
    );

    // Check if input matches base forms (japanese/kana) with optional parts handling
    for (const answer of possibleAnswers) {
      // Generate all combinations from optional parts in parentheses
      const combinations = generateOptionalCombinations(answer);
      
      // Check each combination
      for (const combination of combinations) {
        if (combination === normalizedInput) {
          return true;
        }
      }
    }

    // Check if input matches any included conjugations (both kanji and kana forms)
    if (vocable.conjugations && includedConjugations && includedConjugations.length > 0) {
      const matchingConjugation = includedConjugations.some((formName) => {
        const conjugationValue = vocable.conjugations?.[formName];
        if (!conjugationValue) {
          return false;
        }
        
        const trimmedConjugation = conjugationValue.trim();
        
        // Generate all combinations from optional parts in parentheses for conjugation
        const conjugationCombinations = generateOptionalCombinations(trimmedConjugation);
        
        // Check exact match with any combination of kanji form
        if (conjugationCombinations.some((combo) => combo === normalizedInput)) {
          return true;
        }
        
        // Check if input matches kana form of the conjugation (with optional parts)
        if (vocable.kanjiMapping) {
          for (const combo of conjugationCombinations) {
            const kanaConjugation = convertKanjiToKana(combo, vocable.kanjiMapping);
            if (kanaConjugation && kanaConjugation.trim() === normalizedInput) {
              return true;
            }
          }
        }
        
        return false;
      });
      
      if (matchingConjugation) {
        return true;
      }
    }

    return false;
  }

  const inputLower = userInput.trim().toLowerCase();
  if (!inputLower) {
    return false;
  }

  const englishMeanings = vocable.english ?? [];
  
  return englishMeanings.some((meaning) => {
    const meaningLower = meaning.toLowerCase();
    
    // Generate all combinations from optional parts in parentheses
    const combinations = generateOptionalCombinations(meaningLower);
    
    // Check each combination
    return combinations.some((combination) => {
      const combinationLower = combination.toLowerCase();
      
      // Exact match
      if (combinationLower === inputLower) {
        return true;
      }
      
      // Check Levenshtein distance
      const distance = levenshteinDistance(inputLower, combinationLower);
      const threshold = combinationLower.length * 0.2; // 20% of correct answer length
      
      return distance <= threshold;
    });
  });
}

